import { useEffect } from "react";
import { SITE } from "@/data/site";

function setMeta(attr, key, content) {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export function useSEO({
  title,
  description,
  path = "/",
  noindex = false,
  jsonLd = [],
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, follow" : "index, follow");

    const url = `${SITE.url}${path}`;
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", `${SITE.url}/images/aim/ai-street.jpg`);
    setMeta("name", "twitter:card", "summary_large_image");
    setLink("canonical", url);

    const scripts = [];
    (jsonLd || []).forEach((data, i) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = `jsonld-${i}`;
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      scripts.push(s);
    });

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      scripts.forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, noindex]);
}

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "GeneralContractor",
  "@id": `${SITE.url}/#business`,
  name: SITE.name,
  legalName: SITE.name,
  slogan: "Full-Service Exterior & General Contractor",
  description:
    "AIM High is a licensed & insured full-service exterior and general contractor serving Monmouth County, NJ — roofing, siding, seamless gutters, kitchen & bathroom renovations, basement finishing, and commercial build-outs.",
  image: `${SITE.url}/images/aim/ai-street.jpg`,
  logo: `${SITE.url}/logo-onlight.png`,
  url: SITE.url,
  telephone: SITE.phoneRaw,
  email: SITE.email,
  priceRange: "$$",
  currenciesAccepted: "USD",
  paymentAccepted: "Cash, Check, Credit Card",
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.city,
    addressRegion: SITE.address.state,
    postalCode: SITE.address.zip,
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: SITE.geo.lat,
    longitude: SITE.geo.lng,
  },
  hasMap: SITE.mapsLink,
  areaServed: [
    "Monmouth County, NJ",
    "Union County, NJ",
    "Middlesex County, NJ",
    "Ocean County, NJ",
    "Colts Neck, NJ",
    "Holmdel, NJ",
    "Marlboro, NJ",
    "Freehold, NJ",
    "Middletown, NJ",
    "Manalapan, NJ",
    "Rumson, NJ",
    "Red Bank, NJ",
    "New York City",
  ],
  knowsAbout: [
    "Roofing",
    "Siding",
    "Seamless gutters",
    "Kitchen renovations",
    "Bathroom renovations",
    "Basement finishing",
    "Commercial build-outs",
    "Restaurant build-outs",
    "Retail storefront construction",
    "General contracting",
  ],
  sameAs: [],
});

export const serviceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: service.h1,
  serviceType: service.keyword,
  provider: {
    "@type": "GeneralContractor",
    name: SITE.name,
    telephone: SITE.phoneRaw,
  },
  areaServed: ["Monmouth County, NJ", "Union County, NJ", "Middlesex County, NJ", "Ocean County, NJ"],
  url: `${SITE.url}${service.path}`,
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
